import React, { useEffect, useState } from 'react';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { catalogueAPI, ProductQuoteHistory } from '../../services/api';
import Badge from '../ui/Badge';
import { Card, CardBody } from '../ui/Card';
import Spinner from '../ui/Spinner';

interface ProductQuoteHistoryPanelProps {
  productId: number;
}

const formatMoney = (amount: number, currency: string) =>
  `${currency} ${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getSalesmanName = (salesman?: ProductQuoteHistory['items'][number]['salesman']) => {
  if (!salesman) return '-';
  const fullName = [salesman.firstName, salesman.lastName].filter(Boolean).join(' ');
  return fullName || salesman.email;
};

const ProductQuoteHistoryPanel: React.FC<ProductQuoteHistoryPanelProps> = ({ productId }) => {
  const [history, setHistory] = useState<ProductQuoteHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await catalogueAPI.getProductQuoteHistory(productId);
        setHistory(data);
      } catch (err: any) {
        console.error('Error loading product quote history:', err);
        setError(err.response?.data?.message || 'Failed to load quote history.');
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [productId]);

  return (
    <Card variant="elevated">
      <CardBody className="p-6">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="flex items-center text-lg font-semibold text-neutral-900">
              <DocumentDuplicateIcon className="mr-2 h-5 w-5 text-cyan-600" />
              Quote Price History
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              Actual prices offered for this product on quotes.
            </p>
          </div>

          {history && (
            <div className="flex flex-wrap gap-2">
              <Badge variant="neutral">{history.summary.count} quote lines</Badge>
              {history.summary.byCurrency.map((summary) => (
                <Badge key={summary.currency} variant="primary">
                  Avg {formatMoney(summary.averageUnitPrice, summary.currency)}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-neutral-500">
            <Spinner size="sm" />
            <span className="ml-2 text-sm">Loading quote history...</span>
          </div>
        ) : error ? (
          <div className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
            {error}
          </div>
        ) : !history || history.items.length === 0 ? (
          <p className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">
            No quote history has been recorded for this product yet.
          </p>
        ) : (
          <>
          <div className="space-y-3 md:hidden">
            {history.items.map((item) => (
              <div key={item.id} className="rounded-lg border border-neutral-200 bg-white p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-neutral-900">{item.quoteNumber}</p>
                    <p className="mt-1 truncate text-xs text-neutral-500">{item.client.name}</p>
                  </div>
                  <Badge variant="neutral" size="sm">{item.quoteStatus}</Badge>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs font-medium uppercase text-neutral-500">Unit Price</p>
                    <p className="mt-1 font-semibold text-neutral-900">{formatMoney(item.unitPrice, item.currency)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-neutral-500">Effective</p>
                    <p className="mt-1 font-semibold text-neutral-900">{formatMoney(item.effectiveUnitPrice, item.currency)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-neutral-500">Qty</p>
                    <p className="mt-1 text-neutral-700">{item.quantity} {item.unit}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-neutral-500">Date</p>
                    <p className="mt-1 text-neutral-700">{formatDate(item.quoteCreatedAt)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Quote
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Salesman
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Qty
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Unit Price
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Effective Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 bg-white">
                {history.items.map((item) => (
                  <tr key={item.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-4 text-sm">
                      <div className="font-medium text-neutral-900">{item.quoteNumber}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                        <Badge variant="neutral" size="sm">{item.quoteStatus}</Badge>
                        <span>{item.deal ? item.deal.description : 'Standalone quote'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-neutral-700">{item.client.name}</td>
                    <td className="px-4 py-4 text-sm text-neutral-700">{getSalesmanName(item.salesman)}</td>
                    <td className="px-4 py-4 text-right text-sm text-neutral-700">
                      {item.quantity} {item.unit}
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-neutral-900">
                      {formatMoney(item.unitPrice, item.currency)}
                    </td>
                    <td className="px-4 py-4 text-right text-sm font-semibold text-neutral-900">
                      {formatMoney(item.effectiveUnitPrice, item.currency)}
                    </td>
                    <td className="px-4 py-4 text-sm text-neutral-600">{formatDate(item.quoteCreatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default ProductQuoteHistoryPanel;
